import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RollerLabsLogo from "@/components/RollerLabsLogo";

const LAST_EMAIL_KEY = "last_email";

export default function LoginPage() {
  const savedEmail = localStorage.getItem(LAST_EMAIL_KEY) ?? "";
  const [email, setEmail] = useState(savedEmail);
  const [error, setError] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const { login, isLoading, user, validateSession } = useAuth();
  const navigate = useNavigate();
  const hasValidated = useRef(false);

  const isReturningUser = savedEmail.length > 0;

  useEffect(() => {
    if (!user || hasValidated.current) return;
    hasValidated.current = true;

    setIsValidating(true);
    validateSession()
      .then((valid) => {
        if (valid) {
          navigate(user.role === "admin" ? "/admin" : "/app", { replace: true });
        }
      })
      .finally(() => setIsValidating(false));
  }, [user, validateSession, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    try {
      await login(email);
      navigate("/verify-otp", { state: { email } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send OTP");
    }
  };

  const handleClearEmail = () => {
    localStorage.removeItem(LAST_EMAIL_KEY);
    setEmail("");
  };

  const loading = isLoading || isValidating;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "hsl(var(--roller-dark))" }}>
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <RollerLabsLogo variant="light" size="lg" />
        </div>
        <Card className="border-sidebar-border bg-sidebar-accent">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl font-bold text-center text-sidebar-foreground">
              {isReturningUser ? "Welcome back" : "Welcome"}
            </CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              {isReturningUser
                ? "Enter your verification code to continue"
                : "Enter your email to receive a verification code"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="bg-sidebar border-sidebar-border text-sidebar-foreground placeholder:text-muted-foreground focus-visible:ring-sidebar-ring"
                  autoFocus={!isReturningUser}
                />
                {isReturningUser && (
                  <p className="text-xs text-muted-foreground">
                    Not you?{" "}
                    <button
                      type="button"
                      onClick={handleClearEmail}
                      className="underline hover:text-sidebar-foreground transition-colors"
                    >
                      Use a different account
                    </button>
                  </p>
                )}
              </div>
              {error && (
                <div className="text-sm text-red-400 bg-red-950/30 border border-red-900/30 rounded px-3 py-2">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full font-semibold"
              >
                {loading ? (isValidating ? "Signing you in…" : "Sending…") : "Send verification code"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
